import { Button } from "@/components/ui/Button";
import { CoachModal } from "@/features/coach/components/CoachModal";
import {
  goalStatusLabel,
} from "@/features/coach/goalLabels";
import type { PlayerGoal } from "@/lib/types";
import { viewerGoalContextLabel } from "./GoalCard";

export type GoalDetailsModalProps = {
  goal: PlayerGoal;
  playerName: string;
  onClose: () => void;
  onReflect: (goal: PlayerGoal) => void;
  onOpenSource?: (goal: PlayerGoal) => void;
};

export function GoalDetailsModal({
  goal,
  playerName,
  onClose,
  onReflect,
  onOpenSource,
}: GoalDetailsModalProps) {
  const firstName = playerName.split(/\s+/)[0]?.trim() || "Player";
  const source = sourceSummary(goal);
  const hasOpenableSource = !!(goal.source_note || goal.source_clip);
  const latest = goal.latest_reflection ?? goal.reflections?.[0] ?? null;

  return (
    <CoachModal
      title={`${firstName}’s goal`}
      size="wide"
      onClose={onClose}
      data-testid="feedback-goal-details-modal"
      bodyClassName="family-goal-detail-dialog"
    >
      <article className="family-goal-detail-card">
        <div className="family-goal-detail-heading">
          <p className="eyebrow">{viewerGoalContextLabel(goal.context)}</p>
          <span data-goal-status={goal.status}>
            {goalStatusLabel(goal.status)}
          </span>
        </div>
        <h3>{goal.title || "Player goal"}</h3>
        <div className="family-goal-detail-grid">
          {goal.description ? (
            <section>
              <p className="eyebrow">Action plan</p>
              <p>{goal.description}</p>
            </section>
          ) : null}
          {goal.success_criteria ? (
            <section>
              <p className="eyebrow">Success criteria</p>
              <p>{goal.success_criteria}</p>
            </section>
          ) : null}
        </div>
        {source ? (
          <p className="family-goal-source">
            Source: <strong>{source}</strong>
          </p>
        ) : null}
        {latest ? (
          <section className="family-goal-latest-reflection">
            <p className="eyebrow">Latest reflection</p>
            <p>{latest.reflection}</p>
          </section>
        ) : null}
        <div className="family-goal-detail-actions">
          <Button type="button" onClick={() => onReflect(goal)}>
            Add reflection
          </Button>
          {source && hasOpenableSource && onOpenSource ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenSource(goal)}
            >
              Open source feedback
            </Button>
          ) : null}
        </div>
        <p className="family-goal-detail-help">
          Your coach sets the goal and updates its status. You can share a
          reflection here.
        </p>
      </article>
    </CoachModal>
  );
}

function sourceSummary(goal: PlayerGoal): string | null {
  if (goal.source_note) {
    return (
      goal.source_note.title?.trim() ||
      goal.source_note.event_title?.trim() ||
      `Note #${goal.source_note.id}`
    );
  }
  if (goal.source_clip) {
    return goal.source_clip.title?.trim() || `Clip #${goal.source_clip.id}`;
  }
  if (goal.source_playlist) {
    return goal.source_playlist.title?.trim() || "Coach feedback";
  }
  return null;
}
